/**
 * Inventory Stock History - View stock transactions for an inventory item
 * Displays transaction history from backend API with date filtering
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, TrendingUp, TrendingDown, Calendar, AlertCircle, RefreshCw } from 'lucide-react';
import {
  inventoryApi,
  type Inventory,
  type StockTransaction,
} from '../../../services/gaushala/api';

export default function InventoryStockHistory() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [inventory, setInventory] = useState<Inventory | null>(null);
  const [transactions, setTransactions] = useState<StockTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    if (!id) {
      setError('No inventory ID provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const [inventoryResponse, historyResponse] = await Promise.all([
        inventoryApi.getInventoryById(parseInt(id)),
        inventoryApi.getStockHistory(parseInt(id)),
      ]);

      if (inventoryResponse.success && inventoryResponse.data) {
        setInventory(inventoryResponse.data);
        console.log('📦 Inventory loaded:', inventoryResponse.data.itemName);
      } else {
        console.error('❌ Failed to load inventory:', inventoryResponse.error);
        setError(`Failed to load inventory: ${inventoryResponse.error}`);
      }

      if (historyResponse.success) {
        console.log('📋 Stock history response:', historyResponse.data);
        // Ensure data is an array before setting
        if (Array.isArray(historyResponse.data)) {
          setTransactions(historyResponse.data);
          console.log(`✅ Loaded ${historyResponse.data.length} transactions`);
        } else if (historyResponse.data === null || historyResponse.data === undefined) {
          console.warn('⚠️ Stock history data is null/undefined - No transactions recorded yet');
          setTransactions([]);
        } else {
          console.warn('⚠️ Stock history data is not an array:', historyResponse.data);
          setTransactions([]);
        }
      } else {
        console.error('❌ Failed to load stock history:', historyResponse.error);
        setError(`Failed to load stock history: ${historyResponse.error}`);
        setTransactions([]);
      }
    } catch (error) {
      console.error('❌ Error loading data:', error);
      setError(`Error loading data: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <button
        onClick={() => navigate('/gaushala/inventory')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft size={20} />
        Back to Inventory
      </button>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-4">
          <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-medium text-red-900">Error Loading Stock History</h3>
            <p className="text-sm text-red-700 mt-1">{error}</p>
            <button
              onClick={loadData}
              className="mt-4 flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              <RefreshCw size={16} />
              Retry
            </button>
          </div>
        </div>
      )}

      {inventory && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold">{inventory.itemName}</h1>
          <p className="text-gray-600">Stock Transaction History</p>
          <div className="mt-2 text-sm text-gray-500">
            Current Stock: <span className="font-semibold">{inventory.quantity}</span>
          </div>
        </div>
      )}

      {transactions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <Calendar className="mx-auto text-gray-400" size={48} />
          <p className="mt-4 text-gray-600">No transactions found</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Performed By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {transactions.map((tx, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {new Date(tx.transactionDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {tx.transactionType === 'IN' ? (
                        <>
                          <TrendingUp className="text-green-600" size={18} />
                          <span className="text-green-600 font-medium">Stock In</span>
                        </>
                      ) : (
                        <>
                          <TrendingDown className="text-red-600" size={18} />
                          <span className="text-red-600 font-medium">Stock Out</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {tx.quantity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {tx.performedBy || 'N/A'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {tx.notes || '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
